import React from 'react';
import { Link } from 'react-router-dom';

const SMSTerms = () => {
  return (
    <div 
      className="min-h-screen" 
      style={{ 
        fontFamily: 'Outfit, sans-serif',
        backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
      }}
    >
      {/* Hero Section */}
      <section className="pt-28 lg:pt-32 pb-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-black mb-4"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            SMS Terms & Conditions
          </h1>
          <div className="flex items-center justify-center gap-2 text-base">
            <Link to="/" className="text-pink-600 hover:underline">🏠 Home</Link>
            <span className="text-pink-600">»</span>
            <span className="text-pink-600">SMS Terms</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 px-6 pb-24">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl p-8 lg:p-12">
          <p className="text-gray-600 mb-8">Last Updated: December 2024</p>

          <div className="prose prose-lg max-w-none">
            {/* Program Overview */}
            <div className="bg-pink-50 border-2 border-pink-200 rounded-2xl p-6 mb-8">
              <h2 className="text-2xl font-bold text-pink-900 mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>🍨 Program Overview</h2>
              <p className="text-gray-700">
                <strong>1ST Avenue ICP</strong> (operated by Crazy ICP LLC) offers an SMS messaging program to keep our valued customers informed about special promotions, new menu items, store updates, and exclusive offers.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>1. Consent & Opt-In</h2>
            <p className="text-gray-700 mb-4">
              By providing your phone number and checking the SMS consent checkbox on our website, in-store sign-up form, or by texting a keyword to our number, you expressly consent to receive recurring automated promotional and informational text messages from 1ST Avenue ICP.
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Consent is not required</strong> to make a purchase.</li>
              <li>You must be 18 years of age or older, or have parental consent, to opt in.</li>
              <li>By opting in, you confirm that the phone number provided is yours and that you are authorized to receive text messages at this number.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>2. Message Types & Frequency</h2>
            <p className="text-gray-700 mb-4">
              You may receive the following types of messages:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>🎉 <strong>Promotional Offers:</strong> Weekly specials, discounts, and limited-time offers</li>
              <li>🍦 <strong>New Products:</strong> Announcements about new menu items and flavors</li>
              <li>📅 <strong>Store Updates:</strong> Changes to hours, holiday schedules, and closures</li>
              <li>🎁 <strong>Special Events:</strong> Contests, giveaways, and community events</li>
            </ul>
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-6">
              <p className="text-yellow-800 font-medium">
                📱 <strong>Message Frequency:</strong> Approximately 2-4 messages per month. You may receive additional messages during holidays or special promotions.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>3. Costs & Carrier Fees</h2>
            <p className="text-gray-700 mb-6">
              <strong>Message and data rates may apply.</strong> Standard messaging rates from your wireless carrier may apply to messages sent and received. Please check with your mobile carrier for details about your text messaging plan. We do not charge for the SMS service itself.
            </p>

            {/* Opt-Out Section - Highlighted */}
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 my-8">
              <h2 className="text-2xl font-bold text-red-900 mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>🛑 4. How to Opt Out</h2>
              <p className="text-gray-700 mb-4">
                You can opt out of receiving SMS messages at any time using any of these methods:
              </p>
              <div className="bg-white rounded-xl p-4 mb-4">
                <p className="text-lg font-bold text-red-700 mb-2">Reply STOP</p>
                <p className="text-gray-600">Text <span className="bg-red-100 text-red-700 px-2 py-1 rounded font-mono font-bold">STOP</span> to any message you receive from us.</p>
              </div>
              <p className="text-gray-700 mb-4">
                You will receive a one-time confirmation message: <em>"You have been unsubscribed from 1ST Ave ICP messages. You will not receive any more texts. Reply JOIN to resubscribe."</em>
              </p>
              <p className="text-gray-700">
                <strong>Opt-out requests are processed immediately</strong> and honored indefinitely unless you choose to re-subscribe.
              </p>
            </div>

            {/* Help Section */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 my-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>❓ 5. Help & Support</h2>
              <p className="text-gray-700 mb-4">
                Need assistance? Here's how to get help:
              </p>
              <div className="bg-white rounded-xl p-4 mb-4">
                <p className="text-lg font-bold text-blue-700 mb-2">Reply HELP</p>
                <p className="text-gray-600">Text <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded font-mono font-bold">HELP</span> to any message for assistance.</p>
              </div>
              <p className="text-gray-700">
                Or contact us directly:
              </p>
              <ul className="list-none mt-2 space-y-1">
                <li>📞 Phone: <a href="tel:817-829-4878" className="text-pink-600 hover:underline font-medium">817-829-4878</a></li>
                <li>📧 Email: <a href="mailto:1staveicp@gmail.com" className="text-pink-600 hover:underline font-medium">1staveicp@gmail.com</a></li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>6. Privacy & Data Protection</h2>
            <p className="text-gray-700 mb-4">
              Your privacy is important to us. We are committed to protecting your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>We do NOT sell</strong> your phone number or personal information to third parties.</li>
              <li><strong>We do NOT share</strong> your information for third-party marketing purposes.</li>
              <li>Your phone number is used <strong>solely</strong> for communications from 1ST Avenue ICP.</li>
              <li>We use industry-standard security measures to protect your data.</li>
            </ul>
            <p className="text-gray-700 mb-6">
              For complete details, please review our <Link to="/privacy-policy" className="text-pink-600 hover:underline font-medium">Privacy Policy</Link>.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>7. Supported Carriers</h2>
            <p className="text-gray-700 mb-6">
              Our SMS program is supported by major US carriers including AT&T, Verizon, T-Mobile, Sprint, and most other carriers. However, some carriers may not support all message types. Contact your carrier for more information.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>8. Changes to Terms</h2>
            <p className="text-gray-700 mb-6">
              We reserve the right to modify these SMS Terms & Conditions at any time. Changes will be posted on this page with an updated "Last Updated" date. Your continued participation in the SMS program after changes constitutes acceptance of the new terms.
            </p>

            {/* Business Info */}
            <div className="bg-gray-100 rounded-2xl p-6 mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>Program Sponsor</h2>
              <p className="text-gray-700"><strong>Crazy ICP LLC</strong></p>
              <p className="text-gray-700">DBA: 1ST Avenue ICP</p>
              <p className="text-gray-700">103 NE 1st Ave, Mineral Wells, TX 76066</p>
              <p className="text-gray-700">Phone: <a href="tel:817-829-4878" className="text-pink-600 hover:underline">817-829-4878</a></p>
              <p className="text-gray-700">Email: <a href="mailto:1staveicp@gmail.com" className="text-pink-600 hover:underline">1staveicp@gmail.com</a></p>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t-2 border-gray-200 text-center">
            <Link to="/privacy-policy" className="text-pink-600 hover:underline font-bold mr-6">Privacy Policy</Link>
            <Link to="/contact" className="text-pink-600 hover:underline font-bold">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SMSTerms;
